import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, Divider, alpha, CircularProgress, Zoom, TextField,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import LockIcon from '@mui/icons-material/Lock';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  CaixaTurnoService, ICaixaMovimento, TTipoSangria, IRecebimentoPorForma, IPagamentoDespesa,
} from '../../shared/services/api/Venda/CaixaTurnoService';
import { SessaoCaixaService, IFechamentoConferencia } from '../../shared/services/api/Venda/SessaoCaixaService';
import { AutoCompleteContaBancariaSimple } from '../../shared/forms';
import { PDVThemeColors } from './PDVThemes';
import { PDFExtratoCaixa, PDFSangriaCaixa, PDFTransferenciaCaixa } from './impressaocaixa';
import { VFieldMoney2FaturamentoPercentual } from './VFieldMoney2FaturamentoPercentual';


type TEtapa = 'lista' | 'fechar' | 'sangria' | 'transferencia';

interface DialogFluxoCaixaProps {
  open: boolean;
  onClose: () => void;
  // Conta do caixa da sessão vigente — usada pra excluir ela mesma da lista
  // de destino na transferência entre caixas (o backend já recusa
  // transferir pra si mesmo, mas sem isso a conta aparecia normalmente na
  // busca e só dava erro depois de escolhida).
  idcontaAtual: string;
  dataabertura: string;
  valorabertura: number;
  nomeOperador: string;
  colors: PDVThemeColors;
  onFechado: (fechamento: IFechamentoConferencia) => void;
  onImprimir: (blob: Blob, titulo: string) => void;
  // Etapa em que a dialog já abre (ex.: atalho de Sangria/Fechar Caixa no
  // rodapé pula direto pra etapa, sem passar pela lista). 'lista' se omitido.
  etapaInicial?: TEtapa;
}

const formatMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Card de resumo do topo do relatório (Saldo de abertura/Entradas/Saídas/
// Sangrias/Saldo atual) — `destaque` (Saldo atual) ganha fundo colorido em
// vez de só o ícone, pra chamar mais atenção, igual ao mockup de referência.
const CardResumo: React.FC<{ label: string; valor: number; cor: string; icon: React.ReactNode; destaque?: boolean }> = ({
  label, valor, cor, icon, destaque,
}) => (
  <Box
    sx={{
      flex: '1 1 140px',
      minWidth: 120,
      p: 1.25,
      borderRadius: '10px',
      border: `1px solid ${alpha(cor, destaque ? 0.4 : 0.2)}`,
      bgcolor: alpha(cor, destaque ? 0.1 : 0.05),
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, color: cor }}>
      {icon}
      <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
    <Typography variant="subtitle1" fontWeight={700} sx={{ color: destaque ? cor : 'text.primary' }}>
      R$ {formatMoeda(valor)}
    </Typography>
  </Box>
);

export const DialogFluxoCaixa: React.FC<DialogFluxoCaixaProps> = ({
  open, onClose, idcontaAtual, dataabertura, valorabertura, nomeOperador, colors, onFechado, onImprimir, etapaInicial,
}) => {
  const [carregando, setCarregando] = useState(false);
  const [movimentos, setMovimentos] = useState<ICaixaMovimento[]>([]);
  const [saldoAnterior, setSaldoAnterior] = useState(0);
  const [saldo, setSaldo] = useState(0);
  const [erro, setErro] = useState('');

  const [etapa, setEtapa] = useState<TEtapa>('lista');

  const [fechando, setFechando] = useState(false);
  const [erroFechar, setErroFechar] = useState('');
  const [saldoRealPorForma, setSaldoRealPorForma] = useState<{ categoria: string; valor: number }[]>([]);
  const [entradas, setEntradas] = useState(0);
  const [saidas, setSaidas] = useState(0);
  const [sangrias, setSangrias] = useState(0);
  const [recebimentosPorForma, setRecebimentosPorForma] = useState<IRecebimentoPorForma[]>([]);
  const [pagamentosEDespesas, setPagamentosEDespesas] = useState<IPagamentoDespesa[]>([]);
  // Valor de Dinheiro conferido fisicamente no caixa (único campo do
  // fechamento agora — a pedido, sem escolher conta destino nem dividir por
  // PIX/Cartão/Outros, que já roteiam pra recebível na hora da venda).
  // Pré-preenchido com o saldo do sistema (ver iniciarFechamento) — o
  // operador ajusta se contou diferente.
  const [valorConferido, setValorConferido] = useState(0);

  const [tipoSangria, setTipoSangria] = useState<TTipoSangria | null>(null);
  // Sangria é só em Dinheiro (a pedido): um valor + motivo obrigatório, sem
  // escolher forma de pagamento.
  const [valorSangria, setValorSangria] = useState(0);
  const [motivoSangria, setMotivoSangria] = useState('');
  const motivoSangriaRef = useRef<HTMLInputElement>(null);
  const [enviandoSangria, setEnviandoSangria] = useState(false);
  const [erroSangria, setErroSangria] = useState('');

  const [idContaDestinoTransferencia, setIdContaDestinoTransferencia] = useState('');
  const [nomeContaDestinoTransferencia, setNomeContaDestinoTransferencia] = useState('');
  const [valorTransferencia, setValorTransferencia] = useState(0);
  const [motivoTransferencia, setMotivoTransferencia] = useState('');
  const [enviandoTransferencia, setEnviandoTransferencia] = useState(false);
  const [erroTransferencia, setErroTransferencia] = useState('');

  const carregarFluxo = async () => {
    setCarregando(true);
    setErro('');
    const agora = new Date().toISOString();
    const resultado = await CaixaTurnoService.getFluxo(dataabertura, agora);
    setCarregando(false);

    if (resultado instanceof Error) {
      setErro(resultado.message);
      return;
    }

    const [resumo, linhas] = resultado;
    setMovimentos(linhas);
    setSaldoAnterior(resumo.saldoanterior);
    setSaldo(resumo.saldoreal);
    // Backend antigo (sem saldoRealPorForma) — cai no movimento da sessão.
    setSaldoRealPorForma(resumo.saldoRealPorForma || resumo.porFormaPagamento || []);
    setEntradas(resumo.credito);
    setSaidas(resumo.debito);
    setSangrias(resumo.sangrias);
    setRecebimentosPorForma(resumo.recebimentosPorForma);
    setPagamentosEDespesas(resumo.pagamentosEDespesas);
    // Entrando direto na etapa 'fechar' (atalho do rodapé, sem passar pela
    // lista) — iniciarFechamento não roda nesse caso, então pré-preenche
    // aqui assim que o saldo real chega.
    if (etapaInicial === 'fechar') {
      setValorConferido(resumo.saldoreal);
    }
  };

  // Pré-preenche com o saldo do sistema — o operador ajusta se contou
  // diferente (é essa diferença que vira quebra/sobra de caixa).
  const iniciarFechamento = () => {
    setValorConferido(saldo);
    setErroFechar('');
    setEtapa('fechar');
  };

  // Sangria (cofre ou pagamento) e transferência entre caixas são só em
  // Dinheiro (a pedido) — PIX/Cartão não ficam fisicamente na gaveta, então
  // nem aparecem. O disponível é o Dinheiro de TODO o caixa
  // (`saldoRealPorForma`, inclui sessões anteriores e lançamentos com
  // histórico livre), não só o movimento da sessão. O backend valida de novo
  // antes de gravar, então isso aqui é só orientação visual.
  const disponivelTotalPorCategoria = (categoria: string) => saldoRealPorForma.find(f => f.categoria === categoria)?.valor ?? 0;
  const dinheiroNoCaixa = disponivelTotalPorCategoria('Dinheiro');

  const escolherTipoSangria = (tipo: TTipoSangria) => {
    setTipoSangria(tipo);
    setValorSangria(0);
    setErroSangria('');
  };

  useEffect(() => {
    if (open) {
      setEtapa(etapaInicial ?? 'lista');
      setValorConferido(0);
      setErroFechar('');
      setTipoSangria(null);
      setValorSangria(0);
      setMotivoSangria('');
      setErroSangria('');
      setIdContaDestinoTransferencia('');
      setNomeContaDestinoTransferencia('');
      setValorTransferencia(0);
      setMotivoTransferencia('');
      setErroTransferencia('');
      carregarFluxo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const imprimir = async () => {
    const blob = await PDFExtratoCaixa({
      operador: nomeOperador,
      dataabertura,
      datareferencia: new Date().toISOString(),
      valorabertura,
      movimentos,
      saldo,
    });
    onImprimir(blob, 'Extrato de caixa');
  };

  const confirmarFechamento = async () => {
    if (valorConferido < 0) {
      setErroFechar('O valor conferido não pode ser negativo');
      return;
    }

    setFechando(true);
    setErroFechar('');

    const resultado = await SessaoCaixaService.fecharConferencia(valorConferido);
    if (resultado instanceof Error) {
      setErroFechar(resultado.message);
      setFechando(false);
      return;
    }

    try {
      const blob = await PDFExtratoCaixa({
        operador: nomeOperador,
        dataabertura,
        datareferencia: new Date().toISOString(),
        valorabertura,
        movimentos,
        // Valor que realmente foi transferido (o conferido, não o saldo
        // teórico do sistema — pode ser diferente quando há quebra/sobra).
        saldo: resultado.valorTransferido,
        fechamento: { contadestino: 'Cofre', diferenca: resultado.diferenca },
      });
      onImprimir(blob, 'Fechamento de caixa');
    } catch {
      // Se a impressão falhar, o caixa já foi fechado — não trava o operador por isso.
    }

    setFechando(false);
    onFechado(resultado);
  };

  const confirmarSangria = async () => {
    if (!tipoSangria) {
      setErroSangria('Escolha o tipo de sangria');
      return;
    }
    if (valorSangria <= 0) {
      setErroSangria('Informe o valor da sangria');
      return;
    }
    if (valorSangria > dinheiroNoCaixa + 0.01) {
      setErroSangria(`Valor maior que o disponível no caixa (R$ ${formatMoeda(dinheiroNoCaixa)})`);
      return;
    }
    if (!motivoSangria.trim()) {
      setErroSangria('Informe o motivo da sangria');
      motivoSangriaRef.current?.focus();
      return;
    }

    setEnviandoSangria(true);
    setErroSangria('');

    const resultado = await CaixaTurnoService.sangria(
      tipoSangria,
      [{ categoria: 'Dinheiro', valor: valorSangria }],
      motivoSangria.trim(),
    );
    if (resultado instanceof Error) {
      setErroSangria(resultado.message);
      setEnviandoSangria(false);
      return;
    }

    try {
      const blob = await PDFSangriaCaixa({
        operador: nomeOperador,
        datalan: resultado.datalan,
        valor: resultado.valor,
        motivo: motivoSangria,
        numero: resultado.numero,
        tipo: tipoSangria,
      });
      onImprimir(blob, 'Sangria de caixa');
    } catch {
      // Se a impressão falhar, a sangria já foi registrada — não trava o operador por isso.
    }

    setEnviandoSangria(false);
    setTipoSangria(null);
    setValorSangria(0);
    setMotivoSangria('');
    setEtapa('lista');
    carregarFluxo();
  };

  const confirmarTransferencia = async () => {
    if (!idContaDestinoTransferencia) {
      setErroTransferencia('Selecione o caixa de destino');
      return;
    }
    if (valorTransferencia <= 0) {
      setErroTransferencia('Informe um valor maior que zero');
      return;
    }
    // Só o Dinheiro do caixa (sem PIX/Cartão) — mesmo critério do backend
    // (TransferenciaCaixa.ts).
    const dinheiroDisponivel = disponivelTotalPorCategoria('Dinheiro');
    if (valorTransferencia > dinheiroDisponivel + 0.01) {
      setErroTransferencia(`Valor maior que o disponível em Dinheiro no seu caixa (R$ ${formatMoeda(dinheiroDisponivel)})`);
      return;
    }

    setEnviandoTransferencia(true);
    setErroTransferencia('');

    const resultado = await CaixaTurnoService.transferir(idContaDestinoTransferencia, valorTransferencia, motivoTransferencia);
    if (resultado instanceof Error) {
      setErroTransferencia(resultado.message);
      setEnviandoTransferencia(false);
      return;
    }

    try {
      const blob = await PDFTransferenciaCaixa({
        operador: nomeOperador,
        destino: nomeContaDestinoTransferencia,
        datalan: resultado.datalan,
        valor: resultado.valor,
        motivo: motivoTransferencia,
        numero: resultado.numero,
      });
      onImprimir(blob, 'Transferência de caixa');
    } catch {
      // Se a impressão falhar, a transferência já foi registrada — não trava o operador por isso.
    }

    setEnviandoTransferencia(false);
    setIdContaDestinoTransferencia('');
    setNomeContaDestinoTransferencia('');
    setValorTransferencia(0);
    setMotivoTransferencia('');
    setEtapa('lista');
    carregarFluxo();
  };

  const titulo = etapa === 'fechar' ? 'Finalizar Caixa' : etapa === 'sangria' ? 'Sangria de caixa' : etapa === 'transferencia' ? 'Transferência entre caixas' : 'Fluxo de caixa';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={etapa === 'lista' ? 'md' : 'sm'}
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          overflow: 'hidden',
          // Paper vira uma coluna flex com altura travada: o cabeçalho fica
          // fixo (flexShrink: 0 abaixo) e só o conteúdo rola (overflowY no
          // Box de conteúdo, mais abaixo). Sem isso, em monitor/janela baixa
          // (a lista de resumo + tabelas + últimos movimentos já passa da
          // altura da tela) o conteúdo que não coube ficava simplesmente
          // cortado pelo overflow: hidden — incluindo o botão "Finalizar
          // Caixa", que é o último item da coluna.
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 64px)',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `0.5px solid ${alpha('#000', 0.08)}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: '8px', background: colors.primaryGradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AccountBalanceIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>{titulo}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto', minHeight: 0 }}>
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : erro ? (
          <Typography color="error" variant="body2">{erro}</Typography>
        ) : etapa === 'lista' ? (
          <>
            {/* CARDS DE RESUMO — "Suprimentos" do mockup original ficou de
                fora de propósito — essa operação não existe no sistema
                hoje, mostrar sempre R$ 0,00 seria
                inventar dado. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {/* valorabertura da sessão já vem com o saldo anterior somado
                  (o que já estava no caixa + o que veio do Cofre — ver
                  abrirSessao no backend), a pedido. */}
              <CardResumo label="Saldo de abertura" valor={valorabertura} cor="#1976D2" icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />} />
              <CardResumo label="Entradas" valor={entradas} cor="#388E3C" icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} />
              <CardResumo label="Saídas" valor={saidas} cor="#D32F2F" icon={<TrendingDownIcon sx={{ fontSize: 16 }} />} />
              <CardResumo label="Sangrias" valor={sangrias} cor="#F57C00" icon={<MoneyOffIcon sx={{ fontSize: 16 }} />} />
              <CardResumo label="Saldo atual" valor={saldo} cor={colors.primarySolid} destaque icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />} />
            </Box>

            {!!saldoAnterior && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Na abertura, inclui saldo anterior (não fechado)</Typography>
                <Typography variant="body2" fontWeight={500}>R$ {formatMoeda(saldoAnterior)}</Typography>
              </Box>
            )}

            {/* RECEBIMENTOS POR FORMA DE PAGAMENTO — com quantidade real (ver
                recebimentosPorForma no backend). */}
            {recebimentosPorForma.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Recebimentos por forma de pagamento
                </Typography>
                <Box sx={{ border: `0.5px solid ${alpha('#000', 0.1)}`, borderRadius: '10px', overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Forma</TableCell>
                        <TableCell align="right">Qtde.</TableCell>
                        <TableCell align="right">Valor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recebimentosPorForma.map(forma => (
                        <TableRow key={forma.categoria}>
                          <TableCell>{forma.categoria}</TableCell>
                          <TableCell align="right">{forma.qtde}</TableCell>
                          <TableCell align="right">R$ {formatMoeda(forma.valor)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {recebimentosPorForma.reduce((acc, f) => acc + f.qtde, 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          R$ {formatMoeda(recebimentosPorForma.reduce((acc, f) => acc + f.valor, 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* PAGAMENTOS E DESPESAS — contas pagas dentro desta sessão (ver
                pagamentosEDespesas), sem sangria (já tem card próprio acima). */}
            {!!pagamentosEDespesas?.length && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Pagamentos e despesas
                </Typography>
                <Box sx={{ border: `0.5px solid ${alpha('#000', 0.1)}`, borderRadius: '10px', overflow: 'auto', maxHeight: 220 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Descrição</TableCell>
                        <TableCell>Categoria</TableCell>
                        <TableCell>Forma</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Data/Hora</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagamentosEDespesas.map((despesa, index) => (
                        <TableRow key={index}>
                          <TableCell>{despesa.descricao}</TableCell>
                          <TableCell>{despesa.categoria}</TableCell>
                          <TableCell>{despesa.formaPagamento}</TableCell>
                          <TableCell align="right">R$ {formatMoeda(despesa.valor)}</TableCell>
                          <TableCell>{new Date(despesa.data).toLocaleString('pt-BR')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* ÚLTIMOS MOVIMENTOS — todos os lançamentos de caixa da sessão,
                mais recentes primeiro. */}
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Últimos movimentos
            </Typography>
            <Box sx={{ maxHeight: 240, overflowY: 'auto', mb: 2, border: `0.5px solid ${alpha('#000', 0.1)}`, borderRadius: '10px', px: 1.5 }}>
              {movimentos.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Nenhuma movimentação nesta sessão ainda.
                </Typography>
              ) : (
                movimentos.map((mov, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      py: 0.9, borderBottom: index === movimentos.length - 1 ? 'none' : `0.5px solid ${alpha('#000', 0.06)}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>{mov.historico || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(mov.datalan).toLocaleString('pt-BR')}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: mov.entrada > 0 ? 'success.main' : 'error.main', flexShrink: 0, pl: 1 }}
                    >
                      {mov.entrada > 0 ? `+ R$ ${formatMoeda(mov.entrada)}` : `- R$ ${formatMoeda(mov.saida)}`}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={imprimir}
                sx={{ borderRadius: '10px' }}
              >
                Imprimir
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                startIcon={<MoneyOffIcon />}
                onClick={() => setEtapa('sangria')}
                sx={{ borderRadius: '10px' }}
              >
                Sangria
              </Button>
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={() => setEtapa('transferencia')}
              sx={{ borderRadius: '10px', mb: 1 }}
            >
              Transferir para outro caixa
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<LockIcon />}
              onClick={iniciarFechamento}
              sx={{
                borderRadius: '10px',
                background: colors.primaryGradient,
                color: colors.textOnPrimary,
                '&:hover': { background: colors.hoverGradient },
              }}
            >
              Finalizar Caixa
            </Button>
          </>
        ) : etapa === 'sangria' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Retira valor do caixa. É lançado como uma conta paga em nome do usuário logado.
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Tipo de sangria
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                fullWidth
                variant={tipoSangria === 'cofre' ? 'contained' : 'outlined'}
                onClick={() => escolherTipoSangria('cofre')}
                disabled={enviandoSangria}
                sx={tipoSangria === 'cofre' ? { borderRadius: '10px', background: colors.primaryGradient, color: colors.textOnPrimary } : { borderRadius: '10px' }}
              >
                Para o cofre
              </Button>
              <Button
                fullWidth
                variant={tipoSangria === 'pagamento' ? 'contained' : 'outlined'}
                onClick={() => escolherTipoSangria('pagamento')}
                disabled={enviandoSangria}
                sx={tipoSangria === 'pagamento' ? { borderRadius: '10px', background: colors.primaryGradient, color: colors.textOnPrimary } : { borderRadius: '10px' }}
              >
                Para pagamento
              </Button>
            </Box>

            {tipoSangria && (
              <>
                {/* Só Dinheiro (a pedido): mostra o que tem no caixa e já
                    abre o campo de valor, sem escolher forma de pagamento. */}
                <Box
                  sx={{
                    p: 2, borderRadius: '12px', mb: 2,
                    bgcolor: alpha(colors.primarySolid, 0.06),
                    border: `1px solid ${alpha(colors.primarySolid, 0.2)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">Disponível no caixa (Dinheiro)</Typography>
                  <Typography variant="h6" fontWeight={700}>R$ {formatMoeda(dinheiroNoCaixa)}</Typography>
                </Box>

                <VFieldMoney2FaturamentoPercentual
                  key={tipoSangria}
                  id="valorSangria"
                  label="Valor da sangria (R$)"
                  value={valorSangria}
                  valor={(_id, novoValor) => setValorSangria(Number(novoValor) || 0)}
                  fullWidth
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') motivoSangriaRef.current?.focus(); }}
                />

                <TextField
                  inputRef={motivoSangriaRef}
                  label="Motivo"
                  required
                  value={motivoSangria}
                  onChange={(e) => setMotivoSangria(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmarSangria(); }}
                  size="small"
                  fullWidth
                  disabled={enviandoSangria}
                  sx={{ mt: 2 }}
                />
              </>
            )}

            {erroSangria && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {erroSangria}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
              <Button
                fullWidth
                variant="outlined"
                disabled={enviandoSangria}
                onClick={() => setEtapa('lista')}
                sx={{ borderRadius: '10px' }}
              >
                Voltar
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                disabled={enviandoSangria || !tipoSangria || valorSangria <= 0 || !motivoSangria.trim()}
                onClick={confirmarSangria}
                sx={{ borderRadius: '10px' }}
              >
                {enviandoSangria ? 'Registrando...' : 'Confirmar sangria'}
              </Button>
            </Box>
          </>
        ) : etapa === 'transferencia' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Transfere Dinheiro do seu caixa pro caixa de outro operador (ou outra conta escolhida).
            </Typography>

            <Box
              sx={{
                p: 2, borderRadius: '12px', mb: 2,
                bgcolor: alpha(colors.primarySolid, 0.06),
                border: `1px solid ${alpha(colors.primarySolid, 0.2)}`,
              }}
            >
              <Typography variant="caption" color="text.secondary">Disponível em Dinheiro</Typography>
              <Typography variant="h6" fontWeight={700}>R$ {formatMoeda(disponivelTotalPorCategoria('Dinheiro'))}</Typography>
            </Box>

            <AutoCompleteContaBancariaSimple
              label="Caixa de destino"
              tipo="caixa"
              excludeId={idcontaAtual}
              value={idContaDestinoTransferencia}
              onChange={(id, label) => { setIdContaDestinoTransferencia(id); setNomeContaDestinoTransferencia(label || ''); }}
              disabled={enviandoTransferencia}
            />

            <Box sx={{ mt: 2, mb: 2 }}>
              <VFieldMoney2FaturamentoPercentual
                id="valortransferencia"
                label="Valor a transferir (R$)"
                value={valorTransferencia}
                valor={(_id, novoValor) => setValorTransferencia(Number(novoValor) || 0)}
                fullWidth
              />
            </Box>

            <TextField
              label="Motivo (opcional)"
              value={motivoTransferencia}
              onChange={(e) => setMotivoTransferencia(e.target.value)}
              size="small"
              fullWidth
              disabled={enviandoTransferencia}
            />

            {erroTransferencia && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {erroTransferencia}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
              <Button
                fullWidth
                variant="outlined"
                disabled={enviandoTransferencia}
                onClick={() => setEtapa('lista')}
                sx={{ borderRadius: '10px' }}
              >
                Voltar
              </Button>
              <Button
                fullWidth
                variant="contained"
                disabled={enviandoTransferencia}
                onClick={confirmarTransferencia}
                sx={{
                  borderRadius: '10px',
                  background: colors.primaryGradient,
                  color: colors.textOnPrimary,
                  '&:hover': { background: colors.hoverGradient },
                }}
              >
                {enviandoTransferencia ? 'Transferindo...' : 'Confirmar transferência'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Informe o valor em Dinheiro que você conferiu fisicamente no caixa. Esse valor vai para o Cofre; a diferença em relação ao saldo do sistema é registrada como quebra (ou sobra) de caixa.
            </Typography>

            <Box
              sx={{
                p: 2, borderRadius: '12px', mb: 2,
                bgcolor: alpha(colors.primarySolid, 0.06),
                border: `1px solid ${alpha(colors.primarySolid, 0.2)}`,
              }}
            >
              {!!saldoAnterior && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Saldo anterior (não fechado)</Typography>
                  <Typography variant="body2" fontWeight={600}>R$ {formatMoeda(saldoAnterior)}</Typography>
                </Box>
              )}
              {!!saldoAnterior && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Movimentado nesta sessão</Typography>
                  <Typography variant="body2" fontWeight={600}>R$ {formatMoeda(saldo - saldoAnterior)}</Typography>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">Saldo no sistema (total a transferir)</Typography>
              <Typography variant="h5" fontWeight={700}>R$ {formatMoeda(saldo)}</Typography>
            </Box>

            <VFieldMoney2FaturamentoPercentual
              id="valorConferido"
              label="Valor conferido no caixa (R$)"
              value={valorConferido}
              valor={(_id, novoValor) => setValorConferido(Number(novoValor) || 0)}
              fullWidth
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') confirmarFechamento(); }}
            />

            {Math.abs(valorConferido - saldo) > 0.004 && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1 }}
                color={valorConferido < saldo ? 'error' : 'success.main'}
              >
                {valorConferido < saldo
                  ? `Quebra de caixa: R$ ${formatMoeda(saldo - valorConferido)}`
                  : `Sobra de caixa: R$ ${formatMoeda(valorConferido - saldo)}`}
              </Typography>
            )}

            {erroFechar && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {erroFechar}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
              <Button
                fullWidth
                variant="outlined"
                disabled={fechando}
                onClick={() => setEtapa('lista')}
                sx={{ borderRadius: '10px' }}
              >
                Voltar
              </Button>
              <Button
                fullWidth
                variant="contained"
                disabled={fechando}
                onClick={confirmarFechamento}
                sx={{
                  borderRadius: '10px',
                  background: colors.primaryGradient,
                  color: colors.textOnPrimary,
                  '&:hover': { background: colors.hoverGradient },
                }}
              >
                {fechando ? 'Fechando...' : 'Confirmar e fechar'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};
